import React, { useEffect, useState, useRef } from 'react';
import { getPosts } from '../data/store';
import { getDeviceUid, registerDevice } from '../utils/device';
import CameraCapture from '../components/CameraCapture';

const e = React.createElement;
const GOLD = '#F5C518';
const RADIO_DEFAULT = 100;

const S = {
  page: { minHeight:'100vh', background:'radial-gradient(circle at 50% -10%, #16324f 0%, #0a1628 45%, #050b14 100%)', color:'#fff', display:'flex', flexDirection:'column', alignItems:'center', padding:'40px 16px', fontFamily:'system-ui, -apple-system, sans-serif', boxSizing:'border-box' },
  logoWrap: { display:'flex', flexDirection:'column', alignItems:'center', marginBottom:22 },
  logo: { width:104, height:104, objectFit:'contain', filter:'drop-shadow(0 6px 22px rgba(245,197,24,0.35))', marginBottom:14 },
  title: { fontSize:23, fontWeight:800, color:'#fff', margin:'0 0 6px', letterSpacing:0.3, textAlign:'center' },
  welcome: { fontSize:19, fontWeight:800, color:GOLD, textAlign:'center', margin:'0 0 14px', letterSpacing:0.3 },
  sub: { color:GOLD, fontSize:11.5, marginBottom:0, textAlign:'center', letterSpacing:2, textTransform:'uppercase', fontWeight:700 },
  card: { background:'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))', backdropFilter:'blur(12px)', border:'1px solid rgba(255,255,255,0.09)', borderRadius:22, padding:30, width:'100%', maxWidth:400, boxShadow:'0 25px 70px rgba(0,0,0,0.55)' },
  stepLabel: { textAlign:'center', color:'#c3d0e3', fontSize:14.5, marginBottom:20, fontWeight:500, lineHeight:1.4 },
  pinDots: { display:'flex', justifyContent:'center', gap:16, margin:'4px 0 28px' },
  keypad: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:14 },
  key: { background:'rgba(255,255,255,0.05)', color:'#fff', border:'1px solid rgba(255,255,255,0.1)', borderRadius:14, padding:'20px 0', fontSize:22, fontWeight:700, cursor:'pointer' },
  keyDel: { background:'rgba(220,38,38,0.12)', color:'#f87171', border:'1px solid rgba(220,38,38,0.25)', borderRadius:14, padding:'20px 0', fontSize:13.5, fontWeight:700, cursor:'pointer' },
  btn: { background:'linear-gradient(180deg, #ffd85e, #F5C518)', color:'#151300', border:'none', borderRadius:13, padding:'16px 20px', fontWeight:800, fontSize:15, cursor:'pointer', width:'100%', marginTop:12, boxShadow:'0 10px 24px rgba(245,197,24,0.28)' },
  btnAlt: { background:'transparent', color:'#8aa3c2', border:'1px solid rgba(255,255,255,0.16)', borderRadius:13, padding:'14px 20px', fontWeight:700, fontSize:13.5, cursor:'pointer', width:'100%', marginTop:10 },
  btnGreen: { background:'linear-gradient(180deg, #17b98a, #0f9d72)', color:'#fff', border:'none', borderRadius:13, padding:'16px 20px', fontWeight:800, fontSize:15, cursor:'pointer', width:'100%', marginTop:12, boxShadow:'0 10px 24px rgba(15,157,114,0.3)' },
  btnBlue: { background:'linear-gradient(180deg, #3b82d6, #2568c8)', color:'#fff', border:'none', borderRadius:13, padding:'16px 20px', fontWeight:800, fontSize:15, cursor:'pointer', width:'100%', marginTop:12, boxShadow:'0 10px 24px rgba(37,104,200,0.3)' },
  infoRow: { display:'flex', justifyContent:'space-between', padding:'11px 0', borderBottom:'1px solid rgba(255,255,255,0.08)', fontSize:14 },
  label: { color:'#7f96b3' },
  val: { fontWeight:700 },
  error: { background:'rgba(220,38,38,0.1)', border:'1px solid rgba(220,38,38,0.3)', color:'#f87171', borderRadius:12, padding:'12px 14px', fontSize:13, marginBottom:16 },
  ok: { background:'rgba(15,157,114,0.12)', border:'1px solid rgba(15,157,114,0.35)', color:'#4ade80', borderRadius:12, padding:'12px 14px', fontSize:13, marginBottom:16 },
  select: { width:'100%', padding:'13px', borderRadius:11, background:'rgba(255,255,255,0.05)', color:'#fff', border:'1px solid rgba(255,255,255,0.16)', fontSize:14, marginBottom:12, boxSizing:'border-box' },
};

function dot(filled) {
  return { width:16, height:16, borderRadius:'50%', background: filled ? GOLD : 'rgba(255,255,255,0.12)', border: filled ? 'none' : '1px solid rgba(255,255,255,0.25)', boxShadow: filled ? '0 0 10px rgba(245,197,24,0.6)' : 'none' };
}

function distanciaMetros(lat1, lng1, lat2, lng2) {
  if ([lat1,lng1,lat2,lng2].some((v) => v===null||v===undefined||v===''||Number.isNaN(Number(v)))) return null;
  const R = 6371000;
  const toRad = (d) => (d*Math.PI)/180;
  const dLat = toRad(lat2-lat1);
  const dLng = toRad(lng2-lng1);
  const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLng/2)**2;
  const c = 2*Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R*c;
}

function formatShift12h(shift) {
  if (!shift) return '';
  if (shift.includes('AM') || shift.includes('PM')) return shift.replace('/', ' - ');
  let start = '', end = '';
  if (shift.includes('/')) { const parts = shift.split('/'); start = parts[0]; end = parts[1]; }
  else if (shift.includes('-')) { const parts = shift.split('-'); start = parts[0]; end = parts[1]; }
  else return shift;
  const convert = (time) => {
    if (!time) return '';
    const parts2 = time.split(':');
    let h = parseInt(parts2[0], 10);
    let m = parts2[1] ? String(parts2[1]).padStart(2,'0') : '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return h + ':' + m + ' ' + ampm;
  };
  return convert(start) + ' - ' + convert(end);
}

function horaPR12() {
  return new Date().toLocaleTimeString('en-US', { timeZone: 'America/Puerto_Rico', hour: 'numeric', minute: '2-digit' });
}

export default function Asistencia() {
  const [step, setStep] = useState('pin');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [employee, setEmployee] = useState(null);
  const [shift, setShift] = useState(null);
  const [device, setDevice] = useState(null);
  const [ponchesHoy, setPonchesHoy] = useState([]);
  const [candidates, setCandidates] = useState(null);
  const [posts, setPosts] = useState([]);
  const [specialPostId, setSpecialPostId] = useState('');
  const [specialTurno, setSpecialTurno] = useState('');
  const [tipo, setTipo] = useState(null);
  const [coords, setCoords] = useState(null);
  const [distancia, setDistancia] = useState(null);
  const [geoOk, setGeoOk] = useState(null);
  const [fotoFrontal, setFotoFrontal] = useState(null);
  const [fotoTrasera, setFotoTrasera] = useState(null);
  const startRef = useRef(null);
  const [leafletReady, setLeafletReady] = useState(false);
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);

  useEffect(() => {
    registerDevice();
    getPosts().then(setPosts).catch(() => {});
    // Latido del reloj cada 5 minutos (solo si la pantalla esta visible)
    const latido = setInterval(() => { if (document.visibilityState === 'visible') registerDevice(); }, 5 * 60 * 1000);
    return () => clearInterval(latido);
  }, []);

  useEffect(() => {
    if (window.L) { setLeafletReady(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => setLeafletReady(true);
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (step !== 'geo' || !leafletReady || !coords || !mapRef.current) return;
    const L = window.L;
    if (!mapObjRef.current) {
      mapObjRef.current = L.map(mapRef.current, { zoomControl:false, attributionControl:false, doubleClickZoom:false, scrollWheelZoom:false });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom:19 }).addTo(mapObjRef.current);
    }
    const map = mapObjRef.current;
    map.setView([coords.lat, coords.lng], 17);
    if (map._empMarker) map.removeLayer(map._empMarker);
    map._empMarker = L.marker([coords.lat, coords.lng]).addTo(map).bindPopup('Tu ubicacion');
    if (map._areaCircle) { map.removeLayer(map._areaCircle); map._areaCircle = null; }
    if (shift && shift.lat !== null && shift.lat !== undefined && shift.lat !== '') {
      map._areaCircle = L.circle([shift.lat, shift.lng], { radius: shift.radioMetros || RADIO_DEFAULT, color:'#F5C518', fillOpacity:0.1 }).addTo(map);
      map.fitBounds(map._areaCircle.getBounds());
    }
    setTimeout(() => map.invalidateSize(), 150);
  }, [step, leafletReady, coords, shift]);

  const resetAll = () => {
    setStep('pin'); setPin(''); setError(''); setEmployee(null); setShift(null); setPonchesHoy([]);
    setCandidates(null); setSpecialPostId(''); setSpecialTurno(''); setTipo(null); setCoords(null);
    setDistancia(null); setGeoOk(null); setFotoFrontal(null); setFotoTrasera(null);
    if (mapObjRef.current) { mapObjRef.current.remove(); mapObjRef.current = null; }
  };

  const submitPin = async (pinValue, confirmEmployeeId) => {
    setBusy(true); setError('');
    try {
      const res = await fetch('/api/pin-login', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ pin: pinValue, deviceUid: getDeviceUid(), confirmEmployeeId }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'PIN incorrecto'); setPin(''); setBusy(false); return; }
      if (data.needsConfirmation) { setCandidates(data.candidates); setStep('candidates'); setBusy(false); return; }
      setEmployee(data.employee); setShift(data.shift); setDevice(data.device || null);
      setPonchesHoy(data.ponchesHoy || []);
const devicePuestoIds = (data.device && Array.isArray(data.device.puestoIds)) ? data.device.puestoIds : (data.device && data.device.puestoId != null ? [data.device.puestoId] : []);
            if (!data.shift && data.device && data.device.bloqueadoAPuesto && devicePuestoIds.length === 1) {
                      const soloPost = posts.find((p) => String(p.id) === String(devicePuestoIds[0]));
                      if (soloPost) {
                                  startRef.current = Date.now();
                                  setShift({ scheduleId:null, turno:'Turno especial', postId: soloPost.id, postName: soloPost.name, lat: soloPost.lat, lng: soloPost.lng, radioMetros: soloPost.radioMetros || soloPost.radio_metros });
                                  setStep('tipo');
                                  setBusy(false);
                                  return;
                      }
            }
            if (!data.shift && data.device && data.device.bloqueadoAPuesto && devicePuestoIds.length > 0) {
                      setSpecialPostId(String(devicePuestoIds[0]));
            }
            startRef.current = Date.now();
            setStep(data.shift ? 'confirm' : 'special');
    } catch (err) {
      setError('No se pudo verificar el PIN. Revisa tu conexion.');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const pressDigit = (d) => {
    if (busy) return;
    const next = (pin + d).slice(0,4);
    setPin(next);
    if (next.length === 4) submitPin(next);
  };
  const pressDel = () => setPin((p) => p.slice(0,-1));
  const chooseCandidate = (id) => { submitPin(pin, id); };
  const confirmShiftAndContinue = () => setStep('tipo');
  const lockedToPost = !!(device && device.bloqueadoAPuesto && Array.isArray(device.puestoIds) && device.puestoIds.length > 0);
      const lockedPosts = lockedToPost ? posts.filter((p) => device.puestoIds.map(String).includes(String(p.id))) : [];
  const useSpecialAndContinue = () => {
    if (!specialPostId) { setError('Selecciona un puesto'); return; }
    const post = posts.find((p) => String(p.id) === String(specialPostId));
    if (!post) { setError('Puesto invalido'); return; }
    setShift({ scheduleId:null, turno: specialTurno || 'Turno especial', postId: post.id, postName: post.name, lat: post.lat, lng: post.lng, radioMetros: post.radioMetros || post.radio_metros || RADIO_DEFAULT });
    setError('');
    setStep('tipo');
  };
  const startGeo = () => {
    setGeoOk(null);
    if (!navigator.geolocation) { setError('Este dispositivo no soporta GPS'); return; }
    navigator.geolocation.getCurrentPosition((pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      setCoords({ lat, lng });
      if (shift && shift.lat !== null && shift.lat !== undefined && shift.lat !== '') {
        const d = distanciaMetros(lat, lng, shift.lat, shift.lng);
        setDistancia(d);
        const radio = shift.radioMetros || RADIO_DEFAULT;
        setGeoOk(d !== null && d <= radio);
      } else {
        setGeoOk(true);
        setDistancia(null);
      }
    }, () => { setError('No se pudo obtener tu ubicacion. Activa el GPS e intenta de nuevo.'); }, { enableHighAccuracy:true, timeout:15000 });
  };
  const chooseTipo = (t) => { setTipo(t); setStep('geo'); startGeo(); };
  const submitClock = async () => {
    setStep('submitting'); setError('');
    try {
      const segundos = startRef.current ? Math.round((Date.now() - startRef.current)/1000) : null;
      const res = await fetch('/api/clock', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({
        employeeId: employee.id, employeeName: employee.name, scheduleId: shift.scheduleId, postId: shift.postId, postName: shift.postName,
        turno: shift.turno, tipo, deviceUid: getDeviceUid(), lat: coords.lat, lng: coords.lng, fotoFrontal, fotoTrasera, segundosProceso: segundos,
      }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'No se pudo registrar el ponche'); setStep('review'); return; }
      setStep('done');
    } catch (err) {
      setError('Error de conexion. Intenta de nuevo.');
      setStep('review');
    }
  };

  let stepContent = null;
  if (step === 'pin') {
    stepContent = e('div', null,
      e('p', {style:S.stepLabel}, 'Ingresa tu PIN (ultimos 4 del Seguro Social)'),
      e('div', {style:S.pinDots}, [0,1,2,3].map((i) => e('div', {key:i, style:dot(i < pin.length)}))),
      e('div', {style:S.keypad},
        ['1','2','3','4','5','6','7','8','9'].map((d) => e('button', {key:d, type:'button', style:S.key, onClick:() => pressDigit(d), disabled:busy}, d)),
        e('div', {key:'blank'}),
        e('button', {key:'0', type:'button', style:S.key, onClick:() => pressDigit('0'), disabled:busy}, '0'),
        e('button', {key:'del', type:'button', style:S.keyDel, onClick:pressDel, disabled:busy}, 'Borrar')
      )
    );
  } else if (step === 'candidates') {
    stepContent = e('div', null,
      e('p', {style:S.stepLabel}, 'Varios empleados usan este PIN. Selecciona tu nombre:'),
      (candidates || []).map((c) => e('button', {key:c.id, type:'button', style:S.btnAlt, onClick:() => chooseCandidate(c.id)}, c.name)),
      e('button', {type:'button', style:S.btnAlt, onClick:resetAll}, 'Cancelar')
    );
  } else if (step === 'confirm' && employee && shift) {
    stepContent = e('div', null,
      e('h2', {style:S.welcome}, 'Bienvenid@, ' + employee.name),
      e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Empleado'), e('span', {style:S.val}, employee.name)),
      e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Puesto'), e('span', {style:S.val}, shift.postName)),
      e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Turno'), e('span', {style:S.val}, formatShift12h(shift.turno))),
      e('button', {type:'button', style:S.btnGreen, onClick:confirmShiftAndContinue}, 'Confirmar este turno'),
      e('button', {type:'button', style:S.btnAlt, onClick:() => setStep('special')}, 'Es un turno especial'),
      e('button', {type:'button', style:S.btnAlt, onClick:resetAll}, 'Cancelar')
    );
  } else if (step === 'special' && employee) {
    const singleLockedPost = lockedPosts.length === 1 ? lockedPosts[0] : null;
        stepContent = e('div', null,
                              e('h2', {style:S.welcome}, 'Bienvenid@, ' + employee.name),
                              e('p', {style:S.stepLabel}, shift ? 'Cambiar a un turno especial:' : 'No tienes turno asignado hoy en este puesto:'),
                              singleLockedPost
                                ? e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Puesto'), e('span', {style:S.val}, singleLockedPost.name))
                                : lockedToPost
                                  ? e('select', {style:S.select, value:specialPostId, onChange:(ev) => setSpecialPostId(ev.target.value)},
                                                    e('option', {value:''}, '-- Selecciona un puesto --'),
                                                    lockedPosts.map((p) => e('option', {key:p.id, value:p.id}, p.name))
                                                  )
                                  : e('select', {style:S.select, value:specialPostId, onChange:(ev) => setSpecialPostId(ev.target.value)},
                                                    e('option', {value:''}, '-- Selecciona un puesto --'),
                                                    posts.map((p) => e('option', {key:p.id, value:p.id}, p.name))
                                                  ),
                              !lockedToPost ? e('input', {style:S.select, value:specialTurno, onChange:(ev) => setSpecialTurno(ev.target.value), placeholder:'Describe el turno (ej. 15:00-23:00)'}) : null,
                              e('button', {type:'button', style:S.btnGreen, onClick:useSpecialAndContinue}, 'Continuar'),
                              e('button', {type:'button', style:S.btnAlt, onClick:resetAll}, 'Cancelar')
                            );
    } else if (step === 'tipo') {
          const ultimoPonche = (ponchesHoy && ponchesHoy.length) ? ponchesHoy[ponchesHoy.length - 1] : null;
          const ultimoTipo = ultimoPonche ? ultimoPonche.tipo : null;
          const tipoButtons = [];
          if (!ultimoTipo || ultimoTipo === 'out') {
                    tipoButtons.push(e('button', {key:'in', type:'button', style:S.btnGreen, onClick:() => chooseTipo('in')}, 'Marcar Entrada (Clock In)'));
          } else if (ultimoTipo === 'in') {
                    tipoButtons.push(e('button', {key:'mealout', type:'button', style:S.btnAlt, onClick:() => chooseTipo('meal_out')}, 'Tomar Periodo de Alimentos'));
                    tipoButtons.push(e('button', {key:'out', type:'button', style:S.btnBlue, onClick:() => chooseTipo('out')}, 'Marcar Salida (Clock Out)'));
          } else if (ultimoTipo === 'meal_out') {
                    tipoButtons.push(e('button', {key:'mealin', type:'button', style:S.btnGreen, onClick:() => chooseTipo('meal_in')}, 'Terminar Periodo de Alimentos'));
          } else if (ultimoTipo === 'meal_in') {
                    tipoButtons.push(e('button', {key:'out2', type:'button', style:S.btnBlue, onClick:() => chooseTipo('out')}, 'Marcar Salida (Clock Out)'));
          }
          stepContent = e('div', null,
                                  e('p', {style:S.stepLabel}, 'Que deseas marcar?'),
                                  ...tipoButtons,
                                  e('button', {type:'button', style:S.btnAlt, onClick:resetAll}, 'Cancelar')
                                );
  } else if (step === 'geo') {
    stepContent = e('div', null,
      e('p', {style:S.stepLabel}, 'Verificando tu ubicacion...'),
      coords && e('div', {ref:mapRef, style:{width:'100%', height:180, borderRadius:12, marginBottom:14, background:'#0a1628', overflow:'hidden'}}),
      geoOk === null && e('p', {style:{textAlign:'center', color:'#8aa3c2'}}, 'Buscando senal GPS...'),
      geoOk === true && e('div', {style:S.ok}, 'Dentro del area autorizada' + (distancia !== null ? (' (' + Math.round(distancia) + ' m del puesto)') : '') + '.'),
      geoOk === false && e('div', {style:S.error}, 'Estas a ' + Math.round(distancia) + ' m del puesto. Fuera del area permitida (radio ' + (shift.radioMetros || RADIO_DEFAULT) + ' m). Acercate al puesto.'),
      geoOk === true && e('button', {type:'button', style:S.btnGreen, onClick:() => setStep((tipo==='meal_out'||tipo==='meal_in')?'review':'photoFront')}, 'Continuar'),
      geoOk === false && e('button', {type:'button', style:S.btnAlt, onClick:startGeo}, 'Reintentar ubicacion'),
      e('button', {type:'button', style:S.btnAlt, onClick:resetAll}, 'Cancelar')
    );
  } else if (step === 'photoFront') {
    stepContent = e(CameraCapture, {key:'front', facing:'user', label:'tu rostro (no la tapes)', onCapture:(img) => { setFotoFrontal(img); setStep('photoBack'); }, onCancel:resetAll});
  } else if (step === 'photoBack') {
    stepContent = e(CameraCapture, {key:'back', facing:'environment', label:'el lugar de trabajo', onCapture:(img) => { setFotoTrasera(img); setStep('review'); }, onCancel:resetAll});
  } else if (step === 'review' && employee && shift) {
    stepContent = e('div', null,
      e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Empleado'), e('span', {style:S.val}, employee.name)),
      e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Puesto'), e('span', {style:S.val}, shift.postName)),
            e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Tipo'), e('span', {style:S.val}, tipo === 'in' ? 'Entrada' : tipo === 'out' ? 'Salida' : tipo === 'meal_out' ? 'Salida a Alimentos' : tipo === 'meal_in' ? 'Regreso de Alimentos' : tipo)),
      e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Ubicacion'), e('span', {style:S.val}, geoOk ? 'Verificada' : 'No verificada')),
      e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Hora'), e('span', {style:S.val}, horaPR12())),
      e('div', {style:S.infoRow}, e('span', {style:S.label}, 'Ubicacion GPS'), e('span', {style:{...S.val, fontSize:12}}, coords ? (coords.lat.toFixed(5) + ', ' + coords.lng.toFixed(5)) : '-')),
      e('div', {style:{display:'flex', gap:8, marginTop:10}},
        fotoFrontal && e('img', {src:fotoFrontal, alt:'frontal', style:{width:70, borderRadius:8}}),
        fotoTrasera && e('img', {src:fotoTrasera, alt:'trasera', style:{width:70, borderRadius:8}})
      ),
            e('button', {type:'button', style:S.btnGreen, onClick:submitClock}, 'Confirmar ' + (tipo === 'in' ? 'Clock In' : tipo === 'out' ? 'Clock Out' : tipo === 'meal_out' ? 'Salida a Alimentos' : tipo === 'meal_in' ? 'Regreso de Alimentos' : '')),
      e('button', {type:'button', style:S.btnAlt, onClick:resetAll}, 'Cancelar')
    );
  } else if (step === 'submitting') {
    stepContent = e('p', {style:S.stepLabel}, 'Guardando...');
  } else if (step === 'done') {
    stepContent = e('div', {style:{textAlign:'center'}},
      e('div', {style:S.ok}, 'Ponche registrado correctamente.'),
      e('button', {type:'button', style:S.btnGreen, onClick:resetAll}, 'Listo')
    );
  }

  return e('div', {style:S.page},
    e('div', {style:S.logoWrap},
      e('img', {src:'/Logo_de_GGPC-removebg-preview.png', alt:'GlobalGuard Protection Corp', style:S.logo, onError:(ev) => { ev.target.style.display = 'none'; }}),
      e('div', {style:S.title}, 'Portal del Empleado'),
      e('div', {style:S.sub}, 'Sistema de Clock In / Clock Out')
    ),
    e('div', {style:S.card},
      error && e('div', {style:S.error}, error),
      stepContent
    )
  );
}
